import { useCallback, useState } from 'react'
import { handleNotifications } from '../../utils/handleNotifications'

export type EditingMode = 'create' | 'edit' | null

type UseTableEditingReturn<UiRow> = {
  isFormOpen: boolean
  editingMode: EditingMode
  selectedRow: UiRow | null
  handleOpenCreate: () => void
  handleOpenEdit: (row: UiRow) => void
  handleCloseForm: () => void
  isConfirmDeleteOpen: boolean
  isDeleting: boolean
  handleRequestDelete: () => void
  handleCancelDelete: () => void
  handleConfirmDelete: (remove: (row: UiRow) => Promise<void>) => Promise<void>
}

const useTableEditing = <UiRow>(): UseTableEditingReturn<UiRow> => {
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
  const [editingMode, setEditingMode] = useState<EditingMode>(null)
  const [selectedRow, setSelectedRow] = useState<UiRow | null>(null)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const handleOpenCreate = useCallback((): void => {
    setSelectedRow(null)
    setEditingMode('create')
    setIsFormOpen(true)
  }, [])

  const handleOpenEdit = useCallback((row: UiRow): void => {
    setSelectedRow(row)
    setEditingMode('edit')
    setIsFormOpen(true)
  }, [])

  const handleCloseForm = useCallback((): void => {
    setIsFormOpen(false)
    setEditingMode(null)
    setSelectedRow(null)
    setIsConfirmDeleteOpen(false)
  }, [])

  const handleRequestDelete = useCallback((): void => {
    setIsConfirmDeleteOpen(true)
  }, [])

  const handleCancelDelete = useCallback((): void => {
    setIsConfirmDeleteOpen(false)
  }, [])

  const handleConfirmDelete = useCallback(
    async (remove: (row: UiRow) => Promise<void>): Promise<void> => {
      if (!selectedRow) return
      setIsDeleting(true)
      try {
        await remove(selectedRow)
        setIsConfirmDeleteOpen(false)
        handleCloseForm()
      } catch (error) {
        // 409 — запись связана с другими таблицами (referenced by other objects).
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status === 409) {
          handleNotifications(
            'לא ניתן למחוק את הרשומה מכיוון שהיא בשימוש בטבלאות אחרות',
            { type: 'error' }
          )
        } else {
          handleNotifications('משהו השתבש, נסה שוב', { type: 'error' })
        }
        setIsConfirmDeleteOpen(false)
      } finally {
        setIsDeleting(false)
      }
    },
    [selectedRow, handleCloseForm]
  )

  return {
    isFormOpen,
    editingMode,
    selectedRow,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseForm,
    isConfirmDeleteOpen,
    isDeleting,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
  }
}

export default useTableEditing
